import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { AppHeader } from "../components/AppHeader";
import { toast, Toaster } from "sonner";
import { ArrowLeft, Plus, Search, X, ArrowRightLeft, Users, History, ListChecks } from "lucide-react";
import LevelHistoryDialog from "../components/LevelHistoryDialog";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const inputCls = "w-full px-3 py-2 bg-white border border-[var(--parchment-2)] focus:border-[var(--maroon)] outline-none text-sm";

function MemberRow({ s, levels, currentLevel, onMove, onRemove, onHistory }) {
  return (
    <div className="py-2 flex items-center justify-between gap-2" data-testid={`group-member-${s.id}`}>
      <div className="min-w-0">
        <div className="font-medium text-sm truncate">{s.last_name.toUpperCase()} {s.first_name}</div>
        <div className="text-xs text-[var(--muted)]">{s.class_name || "—"}</div>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        <button
          className="btn-secondary !py-1 !px-1.5"
          onClick={() => onHistory(s)}
          title="Historique des niveaux"
          data-testid={`history-member-${s.id}`}
        >
          <History size={13} />
        </button>
        <select
          className="px-2 py-1 bg-white border border-[var(--parchment-2)] text-xs font-mono-spec outline-none"
          value={currentLevel}
          onChange={(e) => onMove(s, e.target.value)}
          title="Déplacer vers un autre niveau"
          data-testid={`move-member-${s.id}`}
        >
          {levels.map((lv) => <option key={lv} value={lv}>{lv}</option>)}
        </select>
        <button
          className="btn-secondary !py-1 !px-1.5 !text-[var(--late)]"
          onClick={() => onRemove(s)}
          title="Retirer du groupe"
          data-testid={`remove-member-${s.id}`}
        >
          <X size={13} />
        </button>
      </div>
    </div>
  );
}

function StudentSearch({ level, onAdd }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const timer = useRef(null);

  const search = (value) => {
    setQ(value);
    clearTimeout(timer.current);
    if (!value.trim()) {
      setResults([]);
      return;
    }
    timer.current = setTimeout(async () => {
      const { data } = await api.get("/students/search", { params: { q: value } });
      setResults(data);
    }, 250);
  };

  return (
    <div className="mt-3 pt-3 border-t border-[var(--parchment-2)]">
      <div className="relative">
        <Search size={14} className="absolute left-2.5 top-2.5 text-[var(--muted)]" />
        <input
          className={`${inputCls} !pl-8`}
          placeholder="Rechercher un élève à ajouter..."
          value={q}
          onChange={(e) => search(e.target.value)}
          data-testid={`search-student-${level}`}
        />
      </div>
      {results.length > 0 && (
        <div className="mt-1 max-h-48 overflow-y-auto border border-[var(--parchment-2)] divide-y divide-[var(--parchment)] bg-white">
          {results.map((s) => (
            <button
              key={s.id}
              className="w-full px-3 py-2 flex items-center justify-between gap-2 text-left hover:bg-[var(--parchment)]/60 text-sm"
              onClick={() => { onAdd(s); setQ(""); setResults([]); }}
              data-testid={`search-result-${level}-${s.id}`}
            >
              <span>{s.last_name.toUpperCase()} {s.first_name} <span className="text-xs text-[var(--muted)]">· {s.class_name || "—"}</span></span>
              <Plus size={14} className="text-[var(--maroon)] shrink-0" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function RosterBadge({ s, level }) {
  if (s.level === level) {
    return <span className="text-[10px] font-mono-spec text-[var(--muted)]">déjà {level}</span>;
  }
  if (s.level) {
    return (
      <span className="text-[10px] font-mono-spec text-[var(--just)]" title={`Sera déplacé(e) de ${s.level} vers ${level}`}>{s.level}</span>
    );
  }
  return null;
}

function RosterStudentRow({ s, level, checked, onToggle }) {
  const already = s.level === level;
  return (
    <label
      className={`flex items-center gap-3 px-3 py-2 text-sm ${already ? "opacity-50" : "hover:bg-[var(--parchment)]/60 cursor-pointer"}`}
      data-testid={`roster-student-${s.id}`}
    >
      <Checkbox
        checked={already || checked}
        disabled={already}
        onCheckedChange={() => onToggle(s.id)}
        data-testid={`roster-check-${s.id}`}
      />
      <span className="flex-1 truncate">{s.last_name.toUpperCase()} {s.first_name}</span>
      <RosterBadge s={s} level={level} />
    </label>
  );
}

function AddByClassDialog({ level, subjectId, open, onClose, onDone }) {
  const [roster, setRoster] = useState(null);
  const [classId, setClassId] = useState("");
  const [checked, setChecked] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setChecked({});
    api.get("/me/level-groups/roster", { params: { subject_id: subjectId } })
      .then(({ data }) => {
        setRoster(data);
        setClassId((prev) => prev || data[0]?.id || "");
      })
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, [open, subjectId]);

  const current = roster?.find((c) => c.id === classId);
  const ids = Object.keys(checked).filter((id) => checked[id]);
  const toggle = (id) => setChecked((p) => ({ ...p, [id]: !p[id] }));

  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/me/level-groups/members/batch", { student_ids: ids, level, subject_id: subjectId });
      toast.success(`${data.updated} élève(s) ajouté(s) au groupe ${level}`);
      onClose();
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg bg-[var(--cream)]" data-testid={`add-by-class-dialog-${level}`}>
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl text-[var(--maroon)]">
            Cocher des élèves — groupe {level}
          </DialogTitle>
        </DialogHeader>
        {!roster ? (
          <div className="py-6 text-sm text-[var(--ink-soft)]">Chargement...</div>
        ) : (
          <>
            <div className="flex flex-wrap gap-1.5">
              {roster.map((c) => (
                <button
                  key={c.id}
                  className={`px-3 py-1.5 text-xs font-medium border transition ${classId === c.id ? "bg-[var(--maroon)] text-white border-[var(--maroon)]" : "bg-white border-[var(--parchment-2)] hover:border-[var(--maroon)]"}`}
                  onClick={() => setClassId(c.id)}
                  data-testid={`roster-class-${c.name}`}
                >
                  {c.name}
                </button>
              ))}
            </div>
            <div className="max-h-72 overflow-y-auto border border-[var(--parchment-2)] divide-y divide-[var(--parchment)] bg-white">
              {(current?.students || []).map((s) => (
                <RosterStudentRow key={s.id} s={s} level={level} checked={!!checked[s.id]} onToggle={toggle} />
              ))}
              {current && current.students.length === 0 && (
                <div className="px-3 py-4 text-xs text-[var(--muted)] italic">Aucun élève dans cette classe.</div>
              )}
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className="text-xs text-[var(--ink-soft)]" data-testid="roster-selected-count">
                {ids.length} élève{ids.length > 1 ? "s" : ""} coché{ids.length > 1 ? "s" : ""}
              </span>
              <button
                className="btn-primary !py-2 !px-4 text-sm disabled:opacity-40"
                disabled={ids.length === 0 || saving}
                onClick={submit}
                data-testid={`roster-submit-${level}`}
              >
                Ajouter au groupe {level}
              </button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function GroupCardBody({ group, levels, subjectId, onCreate, onMove, onRemove, onAdd, onHistory, onReload }) {
  const [pickerOpen, setPickerOpen] = useState(false);

  if (!group.assignment_id) {
    return (
      <button
        className="mt-4 border-2 border-dashed border-[var(--parchment-2)] hover:border-[var(--maroon)] text-[var(--ink-soft)] hover:text-[var(--maroon)] py-3 flex items-center justify-center gap-2 text-sm font-medium transition"
        onClick={() => onCreate(group.level)}
        data-testid={`create-group-${group.level}`}
      >
        <Plus size={15} /> Créer le groupe {group.level}
      </button>
    );
  }
  if (!group.owned) {
    return (
      <div className="mt-4 text-xs text-[var(--just)] bg-[var(--just)]/10 px-3 py-2" data-testid={`group-not-owned-${group.level}`}>
        Groupe géré par un autre professeur.
      </div>
    );
  }
  return (
    <>
      <div className="divide-y divide-[var(--parchment)]">
        {group.students.map((s) => (
          <MemberRow key={s.id} s={s} levels={levels} currentLevel={group.level} onMove={onMove} onRemove={onRemove} onHistory={onHistory} />
        ))}
        {group.students.length === 0 && (
          <div className="py-3 text-xs text-[var(--muted)] italic">Aucun élève — utilisez la recherche ci-dessous.</div>
        )}
      </div>
      <StudentSearch level={group.level} onAdd={(s) => onAdd(s, group.level)} />
      <button
        className="mt-3 w-full border border-[var(--parchment-2)] hover:border-[var(--maroon)] text-[var(--ink-soft)] hover:text-[var(--maroon)] py-2 flex items-center justify-center gap-2 text-sm font-medium transition bg-white"
        onClick={() => setPickerOpen(true)}
        data-testid={`add-by-class-btn-${group.level}`}
      >
        <ListChecks size={15} /> Cocher des élèves par classe
      </button>
      <AddByClassDialog level={group.level} subjectId={subjectId} open={pickerOpen} onClose={() => setPickerOpen(false)} onDone={onReload} />
    </>
  );
}

function LevelGroupCard({ group, levels, subjectId, onCreate, onMove, onRemove, onAdd, onHistory, onReload }) {
  return (
    <div className="bg-white border border-[var(--parchment-2)] p-5 flex flex-col" data-testid={`level-group-card-${group.level}`}>
      <div className="flex items-center justify-between pb-3 border-b border-[var(--parchment-2)]">
        <div className="font-serif-display text-2xl font-semibold text-[var(--maroon)]">{group.level}</div>
        <span className="text-xs text-[var(--ink-soft)] inline-flex items-center gap-1">
          <Users size={13} /> {group.students.length} élève{group.students.length > 1 ? "s" : ""}
        </span>
      </div>
      <GroupCardBody
        group={group}
        levels={levels}
        subjectId={subjectId}
        onCreate={onCreate}
        onMove={onMove}
        onRemove={onRemove}
        onAdd={onAdd}
        onHistory={onHistory}
        onReload={onReload}
      />
    </div>
  );
}

const SCALE_LABELS = { ANG: "échelle Cambridge", CHI: "échelle HSK" };

export default function TeacherGroups() {
  const [subjects, setSubjects] = useState([]);
  const [subjectId, setSubjectId] = useState("");
  const [data, setData] = useState(null);
  const [historyFor, setHistoryFor] = useState(null);

  useEffect(() => {
    api.get("/me/level-subjects")
      .then(({ data }) => {
        setSubjects(data);
        setSubjectId((prev) => prev || data[0]?.subject?.id || "");
      })
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, []);

  const load = useCallback(async () => {
    if (!subjectId) return;
    try {
      const { data } = await api.get("/me/level-groups", { params: { subject_id: subjectId } });
      setData(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }, [subjectId]);

  useEffect(() => {
    load();
  }, [load]);

  const createGroup = async (level) => {
    try {
      await api.post("/me/level-groups", { level, subject_id: subjectId });
      toast.success(`Groupe ${level} créé`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const setMember = async (student, level, successMsg) => {
    try {
      await api.put("/me/level-groups/members", { student_id: student.id, level, subject_id: subjectId });
      toast.success(successMsg);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--cream)]">
      <AppHeader />
      <Toaster richColors position="top-right" />
      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <Link to="/teacher" className="inline-flex items-center gap-2 text-sm text-[var(--ink-soft)] hover:text-[var(--maroon)] transition-colors" data-testid="back-link">
          <ArrowLeft size={16} /> Retour au tableau de bord
        </Link>
        <div className="mt-6">
          <div className="eyebrow flex items-center gap-2"><ArrowRightLeft size={13} /> {data?.subject?.name || "Langue"} — {SCALE_LABELS[data?.subject?.code] || "échelle CECRL"}</div>
          <h1 className="font-serif-display text-4xl sm:text-5xl font-semibold mt-2" data-testid="groups-title">Mes groupes de niveaux</h1>
          <p className="mt-3 text-[var(--ink-soft)] max-w-2xl">
            Créez vos groupes, ajoutez des élèves depuis la base globale et déplacez-les d'un niveau à l'autre en cours d'année.
            Les groupes servent aux progressions et aux rattrapages ; l'appel se fait par classe réelle depuis la page Appel.
          </p>
        </div>

        {subjects.length > 1 && (
          <div className="mt-8 flex flex-wrap gap-2" data-testid="group-subject-tabs">
            {subjects.map((s) => (
              <button
                key={s.subject.id}
                onClick={() => { setSubjectId(s.subject.id); setData(null); }}
                className={`px-4 py-2 text-sm font-medium border transition ${subjectId === s.subject.id ? "bg-[var(--maroon)] text-white border-[var(--maroon)]" : "bg-white text-[var(--ink-soft)] border-[var(--parchment-2)] hover:border-[var(--maroon)]"}`}
                data-testid={`subject-tab-${s.subject.code}`}
              >
                {s.subject.name}
              </button>
            ))}
          </div>
        )}

        {!data ? (
          <div className="mt-10 text-[var(--ink-soft)]">Chargement...</div>
        ) : (
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="level-groups-grid">
            {data.groups.map((g) => (
              <LevelGroupCard
                key={g.level}
                group={g}
                levels={data.levels}
                subjectId={subjectId}
                onCreate={createGroup}
                onMove={(s, lv) => setMember(s, lv, `${s.first_name} déplacé(e) vers ${lv}`)}
                onRemove={(s) => setMember(s, null, `${s.first_name} retiré(e) du groupe`)}
                onAdd={(s, lv) => setMember(s, lv, `${s.first_name} ajouté(e) au groupe ${lv}`)}
                onHistory={setHistoryFor}
                onReload={load}
              />
            ))}
          </div>
        )}
        {historyFor && <LevelHistoryDialog student={historyFor} onClose={() => setHistoryFor(null)} />}
      </main>
    </div>
  );
}
